import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Container, Typography, Box, Button, Alert } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EmailIcon from "@mui/icons-material/Email";
import { Search as SearchIcon } from "@mui/icons-material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { useOutreachData } from "../../hooks/useOutreachData";
import { useOutreachAnalytics } from "../../hooks/useOutreachAnalytics";
import { useOutreachForm, OutreachFormItem } from "../../hooks/useOutreachForm";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import {
  GridColumnVisibilityModel,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";

import { OutreachDataGrid } from "../../components/outreach/OutreachDataGrid";
import { OutreachFormDialog } from "../../components/outreach/OutreachFormDialog";
import { OutreachSettingsDrawer } from "../../components/outreach/OutreachSettingsDrawer";
import { OutreachEmailModal } from "../../components/outreach/OutreachEmailModal";
import { YouTubeSearchModal } from "../../components/outreach/YouTubeSearchModal";
import { SpotifyPlaylistSearchModal } from "../../components/outreach/SpotifyPlaylistSearchModal";
import CampaignsTab from "../../components/outreach/CampaignsTab";
import {
  fetchYouTubeChannelDetails,
  fetchYouTubeAnalytics,
} from "../../services/outreach/integrations";
import {
  OutreachCategory,
  categoryDisplayNames,
  Campaign,
  ReactionChannel,
  PlaylistingChannel,
} from "../../types/outreach";
import {
  getTabIndexFromRoute,
  getCategoryFromRoute,
  DEFAULT_OUTREACH_ROUTE,
} from "../../utils/outreachRouting";

const OutreachPage: React.FC = () => {
  const { view } = useParams<{ view?: string }>();
  const navigate = useNavigate();

  // Determine active tab from URL route
  const activeTab = useMemo(() => {
    const routeView = view || DEFAULT_OUTREACH_ROUTE;
    const tabIndex = getTabIndexFromRoute(routeView);
    return tabIndex >= 0 ? tabIndex : 0;
  }, [view]);

  // Redirect if invalid route is accessed
  useEffect(() => {
    if (view && getTabIndexFromRoute(view) < 0) {
      // Invalid route, redirect to default view
      navigate(`/outreach/${DEFAULT_OUTREACH_ROUTE}`, { replace: true });
    }
  }, [view, navigate]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [alertMessage, setAlertMessage] = useState<{
    message: string;
    severity: "success" | "error" | "info" | "warning";
  } | null>(null);
  const [youtubeSearchOpen, setYoutubeSearchOpen] = useState<boolean>(false);
  const [spotifySearchOpen, setSpotifySearchOpen] = useState<boolean>(false);

  // State for column visibility and sort model with localStorage persistence
  const [columnVisibility, setColumnVisibility] = useLocalStorageState<{
    [tabIndex: number]: GridColumnVisibilityModel;
  }>("outreach_column_visibility", {
    // Hide Monthly Visits column for Music Blogs tab (index 3)
    3: { monthlyVisits: false },
  });

  const [sortModel, setSortModel] = useLocalStorageState<{
    [tabIndex: number]: GridSortModel;
  }>("outreach_sort_model", {});

  // Use custom hooks
  const outreachData = useOutreachData();

  // Current column visibility and sort model for active tab
  const currentColumnVisibility = columnVisibility[activeTab] || {};
  const currentSortModel = sortModel[activeTab] || [];

  // Get current category based on active tab
  const getCurrentCategory = (): OutreachCategory => {
    switch (activeTab) {
      case 0:
        return OutreachCategory.REACTION_CHANNELS;
      case 1:
        return OutreachCategory.PLAYLISTING_CHANNELS;
      case 2:
        return OutreachCategory.SPOTIFY_PLAYLISTS;
      case 3:
        return OutreachCategory.MUSIC_BLOGS;
      case 4:
        return OutreachCategory.CAMPAIGNS;
      default:
        return OutreachCategory.REACTION_CHANNELS;
    }
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 0:
        return outreachData.reactionChannels;
      case 1:
        return outreachData.playlistingChannels;
      case 2:
        return outreachData.spotifyPlaylists;
      case 3:
        return outreachData.musicBlogs;
      case 4:
        return outreachData.campaigns || [];
      default:
        return [];
    }
  };

  // Get selected items based on selected row IDs
  const selectedItems = useMemo(() => {
    const currentData = getCurrentData();
    return currentData.filter((item) => selectedRows.includes(item.id));
  }, [selectedRows, activeTab, outreachData]);

  const [channelAnalyticsStatus, setChannelAnalyticsStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [channelAnalyticsMessage, setChannelAnalyticsMessage] = useState<
    string | null
  >(null);
  const {
    dialogOpen,
    dialogMode,
    currentItem,
    openAddDialog,
    openEditDialog,
    closeDialog,
    handleFieldChange,
    getEmptyItem,
  } = useOutreachForm({ currentCategory: getCurrentCategory() });

  useEffect(() => {
    if (!dialogOpen) {
      setChannelAnalyticsStatus("idle");
      setChannelAnalyticsMessage(null);
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) return;
    setChannelAnalyticsStatus("idle");
    setChannelAnalyticsMessage(null);
  }, [currentItem?.channelUrl, dialogOpen]);

  useEffect(() => {
    const category = getCurrentCategory();
    if (
      category !== OutreachCategory.REACTION_CHANNELS &&
      category !== OutreachCategory.PLAYLISTING_CHANNELS
    ) {
      setChannelAnalyticsStatus("idle");
      setChannelAnalyticsMessage(null);
    }
  }, [activeTab]);

  // Refresh data callback
  const handleRefreshData = useCallback(() => {
    outreachData.refreshData();
  }, [outreachData]);

  const handleOpenYoutubeSearch = () => {
    setYoutubeSearchOpen(true);
  };

  const handleCloseYoutubeSearch = () => {
    setYoutubeSearchOpen(false);
  };

  const handleOpenSpotifySearch = () => {
    setSpotifySearchOpen(true);
  };

  const handleCloseSpotifySearch = () => {
    setSpotifySearchOpen(false);
  };

  const handleChannelAdded = () => {
    outreachData.refreshData();
  };

  const showYouTubeSearch = activeTab === 0 || activeTab === 1;
  const showSpotifySearch =
    getCurrentCategory() === OutreachCategory.SPOTIFY_PLAYLISTS;

  const handleApplyChannelAnalytics = async () => {
    if (!currentItem) {
      return;
    }

    const category = getCurrentCategory();

    if (
      category !== OutreachCategory.REACTION_CHANNELS &&
      category !== OutreachCategory.PLAYLISTING_CHANNELS
    ) {
      return;
    }

    if (!currentItem.channelUrl) {
      setChannelAnalyticsStatus("error");
      setChannelAnalyticsMessage("Enter a channel URL to apply analytics.");
      return;
    }

    setChannelAnalyticsStatus("loading");
    setChannelAnalyticsMessage(null);

    try {
      const [details, analyticsData] = await Promise.all([
        fetchYouTubeChannelDetails(currentItem.channelUrl),
        fetchYouTubeAnalytics(currentItem.channelUrl),
      ]);

      if (!details && !analyticsData) {
        throw new Error("Unable to fetch analytics for this channel.");
      }

      const channelName = details?.title ?? currentItem.channelName;
      const subscriberCount =
        analyticsData?.subscriberCount ??
        details?.subscriberCount ??
        currentItem.subscriberCount;
      const viewCount = analyticsData?.viewCount ?? details?.viewCount;
      const videoCount = analyticsData?.videoCount ?? details?.videoCount;
      const likes = details?.likes;
      const lastUpdated = new Date().toISOString();

      if (channelName) {
        handleFieldChange("channelName", channelName);
      }
      if (subscriberCount) {
        handleFieldChange("subscriberCount", subscriberCount);
      }
      if (typeof viewCount === "number") {
        handleFieldChange("viewCount", viewCount);
      }
      if (typeof videoCount === "number") {
        handleFieldChange("videoCount", videoCount);
      }
      if (likes) {
        handleFieldChange("likes", likes);
      }
      handleFieldChange("lastUpdated", lastUpdated);

      if (dialogMode === "edit" && currentItem.id) {
        const updateData: Partial<ReactionChannel> &
          Partial<PlaylistingChannel> = {
          ...(channelName ? { channelName } : {}),
          ...(subscriberCount ? { subscriberCount } : {}),
          ...(typeof viewCount === "number" ? { viewCount } : {}),
          ...(typeof videoCount === "number" ? { videoCount } : {}),
          ...(likes ? { likes } : {}),
          lastUpdated,
        };

        await outreachData.updateItem(
          category,
          currentItem.id,
          updateData as any
        );
      }

      setChannelAnalyticsStatus("success");
      setChannelAnalyticsMessage("Channel analytics applied successfully.");
    } catch (error) {
      console.error("Error applying channel analytics:", error);
      setChannelAnalyticsStatus("error");
      setChannelAnalyticsMessage(
        error instanceof Error
          ? error.message
          : "Failed to apply channel analytics."
      );
    }
  };

  // Analytics hooks
  const analytics = useOutreachAnalytics(
    outreachData.reactionChannels,
    outreachData.playlistingChannels,
    outreachData.spotifyPlaylists,
    outreachData.musicBlogs,
    handleRefreshData
  );

  // Handle column visibility change
  const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibility({
      ...columnVisibility,
      [activeTab]: model,
    });
  };

  // Handle row selection change
  const handleRowSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setSelectedRows(newSelection);
    },
    []
  );

  // Handle removing an item from selection
  const handleRemoveFromSelection = (id: string) => {
    setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
  };

  // Handle sort model change
  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel({
      ...sortModel,
      [activeTab]: model,
    });
  };

  const handleUpdateChannelMetadata = useCallback(
    async (ids: string[], updates: Record<string, any>) => {
      const category = getCurrentCategory();

      if (
        category !== OutreachCategory.REACTION_CHANNELS &&
        category !== OutreachCategory.PLAYLISTING_CHANNELS &&
        category !== OutreachCategory.SPOTIFY_PLAYLISTS
      ) {
        return;
      }

      const isSpotifyCategory = category === OutreachCategory.SPOTIFY_PLAYLISTS;
      const singularLabel = isSpotifyCategory ? "Playlist" : "Channel";
      const pluralLabel = isSpotifyCategory ? "Playlists" : "Channels";

      try {
        await Promise.all(
          ids.map((id) => outreachData.updateItem(category, id, updates))
        );
        setAlertMessage({
          message:
            ids.length === 1
              ? `${singularLabel} updated successfully`
              : `${pluralLabel} updated successfully`,
          severity: "success",
        });
      } catch (error) {
        console.error("Error updating outreach item(s):", error);
        setAlertMessage({
          message: `Error updating ${ids.length === 1 ? singularLabel.toLowerCase() : pluralLabel.toLowerCase()}`,
          severity: "error",
        });
        throw error;
      }
    },
    [activeTab, outreachData]
  );

  // Handle form submission
  const handleSubmit = async () => {
    if (!currentItem) return;

    const category = getCurrentCategory();
    try {
      if (dialogMode === "add") {
        // Ensure all required fields are present
        const itemToAdd = {
          ...currentItem,
          genre: currentItem.genre || "Any", // Provide default for required fields
        };

        await outreachData.addItem(category, itemToAdd as any);
        setAlertMessage({
          message: "Item added successfully",
          severity: "success",
        });
      } else if (currentItem.id) {
        const { id, ...itemData } = currentItem;
        // Ensure all required fields are present
        const itemToUpdate = {
          ...itemData,
          genre: itemData.genre || "Any", // Provide default for required fields
        };

        await outreachData.updateItem(category, id, itemToUpdate as any);
        setAlertMessage({
          message: "Item updated successfully",
          severity: "success",
        });
      }
      closeDialog();
    } catch (error) {
      console.error("Error saving item:", error);
      setAlertMessage({
        message: "Error saving item",
        severity: "error",
      });
    }
  };

  // Handle YouTube analytics refresh
  const handleRefreshYouTube = async () => {
    try {
      const result = await analytics.refreshYouTubeAnalytics();
      setAlertMessage({
        message: `Successfully updated ${result.successCount} of ${result.totalChannels} YouTube channels.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error refreshing YouTube analytics:", error);
      setAlertMessage({
        message: "Error refreshing YouTube analytics",
        severity: "error",
      });
    }
  };

  // Handle Spotify analytics refresh
  const handleRefreshSpotify = async () => {
    try {
      const result = await analytics.refreshSpotifyAnalytics();
      setAlertMessage({
        message: `Successfully updated ${result.successCount} of ${result.totalPlaylists} Spotify playlists.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error refreshing Spotify analytics:", error);
      setAlertMessage({
        message: "Error refreshing Spotify analytics",
        severity: "error",
      });
    }
  };

  // Handle Music Blogs analytics refresh
  const handleRefreshMusicBlogs = async () => {
    try {
      const result = await analytics.refreshMusicBlogsAnalytics();
      setAlertMessage({
        message: `Successfully updated ${result.successCount} of ${result.totalBlogs} Music Blogs.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error refreshing Music Blogs analytics:", error);
      setAlertMessage({
        message: "Error refreshing Music Blogs analytics",
        severity: "error",
      });
    }
  };

  // Handle import complete
  const handleImportComplete = () => {
    outreachData.refreshData();
    setAlertMessage({
      message: "Data imported successfully",
      severity: "success",
    });
  };

  // Clear alert message
  const handleAlertClose = () => {
    setAlertMessage(null);
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        height: 'calc(100vh - 64px)', // Account for app bar height
        display: 'flex',
        flexDirection: 'column',
        py: 4,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        {/*  <Typography variant="h4" component="h1" gutterBottom>
          Outreach
        </Typography> */}

        {alertMessage && (
          <Alert
            severity={alertMessage.severity}
            onClose={handleAlertClose}
            sx={{ mb: 2, flexShrink: 0 }}
          >
            {alertMessage.message}
          </Alert>
        )}

        <Box sx={{ width: "100%", display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          {/* Render content based on active tab/route */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <Typography variant="h5" sx={{ mr: 2 }}>
              {categoryDisplayNames[getCurrentCategory()]}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {selectedRows.length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<EmailIcon />}
                  onClick={() => {
                    if (selectedItems.length > 0) {
                      // Get category path for URL
                      const getCategoryPath = () => {
                        switch (getCurrentCategory()) {
                          case OutreachCategory.REACTION_CHANNELS:
                            return "reaction-channels";
                          case OutreachCategory.PLAYLISTING_CHANNELS:
                            return "playlisting-channels";
                          case OutreachCategory.SPOTIFY_PLAYLISTS:
                            return "spotify-playlists";
                          case OutreachCategory.MUSIC_BLOGS:
                            return "music-blogs";
                          case OutreachCategory.CAMPAIGNS:
                            return "campaigns";
                          default:
                            return "reaction-channels";
                        }
                      };

                      // Store selected items in localStorage
                      localStorage.setItem(
                        "selectedCampaignItems",
                        JSON.stringify(selectedItems)
                      );

                      // Navigate to campaign details page with the first item's ID
                      // We'll use this ID to identify the category, but will load all selected items
                      window.location.href = `/outreach/campaign/${getCategoryPath()}/${
                        selectedItems[0].id
                      }`;
                    }
                  }}
                >
                  Start Campaign ({selectedRows.length})
                </Button>
              )}
              {showYouTubeSearch && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleOpenYoutubeSearch}
                >
                  Search YouTube
                </Button>
              )}
              {showSpotifySearch && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MusicNoteIcon />}
                  onClick={handleOpenSpotifySearch}
                >
                  Search Spotify Playlists
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAddDialog}
              >
                Add {categoryDisplayNames[getCurrentCategory()].slice(0, -1)}
              </Button>
            </Box>
          </Box>

          {/* Render correct content based on current category */}
          <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
            {getCurrentCategory() === OutreachCategory.CAMPAIGNS ? (
              <CampaignsTab
                campaigns={getCurrentData() as Campaign[]}
                loading={outreachData.loading}
                onEdit={openEditDialog}
                onDelete={(id) =>
                  outreachData.deleteItem(OutreachCategory.CAMPAIGNS, id)
                }
              />
            ) : (
              <OutreachDataGrid
                category={getCurrentCategory()}
                data={getCurrentData()}
                loading={outreachData.loading}
                onEdit={openEditDialog}
                onDelete={(id) =>
                  outreachData.deleteItem(getCurrentCategory(), id)
                }
                columnVisibility={currentColumnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                sortModel={currentSortModel}
                onSortModelChange={handleSortModelChange}
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={handleRowSelectionChange}
                onUpdateChannels={handleUpdateChannelMetadata}
              />
            )}
          </Box>
        </Box>

        {/* Form Dialog */}
        <OutreachFormDialog
          open={dialogOpen}
          mode={dialogMode}
          category={getCurrentCategory()}
          currentItem={currentItem}
          onClose={closeDialog}
          onSubmit={handleSubmit}
          onFieldChange={handleFieldChange}
          onApplyChannelAnalytics={
            getCurrentCategory() === OutreachCategory.REACTION_CHANNELS ||
            getCurrentCategory() === OutreachCategory.PLAYLISTING_CHANNELS
              ? handleApplyChannelAnalytics
              : undefined
          }
          analyticsStatus={channelAnalyticsStatus}
          analyticsMessage={channelAnalyticsMessage}
        />

        {/* Email Modal */}
        <OutreachEmailModal
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          selectedItems={selectedItems}
          category={getCurrentCategory()}
          visibleColumns={Object.keys(currentColumnVisibility).filter(
            (key) => currentColumnVisibility[key]
          )}
          onRemoveItem={handleRemoveFromSelection}
          campaignId={`outreach_${getCurrentCategory()}_${Date.now()}`} // Generate campaign ID based on category
        />

        {/* Settings Drawer */}
        <OutreachSettingsDrawer
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          refreshingYouTube={analytics.refreshingYouTube}
          refreshingSpotify={analytics.refreshingSpotify}
          refreshingMusicBlogs={analytics.refreshingMusicBlogs}
          onRefreshYouTube={handleRefreshYouTube}
          onRefreshSpotify={handleRefreshSpotify}
          onRefreshMusicBlogs={handleRefreshMusicBlogs}
          columns={useMemo(() => {
            // Get columns for the current category
            const category = getCurrentCategory();
            switch (category) {
              case OutreachCategory.REACTION_CHANNELS:
                return [
                  { field: "channelName", headerName: "Channel Name" },
                  { field: "subscriberCount", headerName: "Subscriber Count" },
                  { field: "viewCount", headerName: "Total Views" },
                  { field: "videoCount", headerName: "Videos" },
                  { field: "likes", headerName: "Likes" },
                  { field: "engagement", headerName: "Engagement" },
                  { field: "genre", headerName: "Genre" },
                  { field: "email", headerName: "Email" },
                  { field: "socialHandles", headerName: "Social Media" },
                  { field: "actions", headerName: "Actions" },
                ];
              case OutreachCategory.PLAYLISTING_CHANNELS:
                return [
                  { field: "channelName", headerName: "Channel Name" },
                  { field: "subscriberCount", headerName: "Subscriber Count" },
                  { field: "viewCount", headerName: "Total Views" },
                  { field: "videoCount", headerName: "Videos" },
                  { field: "likes", headerName: "Likes" },
                  { field: "engagement", headerName: "Engagement" },
                  { field: "genre", headerName: "Genre" },
                  { field: "email", headerName: "Email" },
                  { field: "actions", headerName: "Actions" },
                ];
              case OutreachCategory.SPOTIFY_PLAYLISTS:
                return [
                  { field: "playlistName", headerName: "Playlist Name" },
                  { field: "playlistLikes", headerName: "Likes" },
                  { field: "playlistFollowers", headerName: "Followers" },
                  { field: "trackCount", headerName: "Tracks" },
                  { field: "genre", headerName: "Genre" },
                  { field: "curatorSocialHandle", headerName: "Social Media" },
                  { field: "actions", headerName: "Actions" },
                ];
              case OutreachCategory.MUSIC_BLOGS:
                return [
                  { field: "blogName", headerName: "Blog" },
                  { field: "contact", headerName: "Contact" },
                  { field: "genre", headerName: "Genre" },
                  { field: "monthlyVisits", headerName: "Monthly Visits" },
                  { field: "actions", headerName: "Actions" },
                ];
              case OutreachCategory.CAMPAIGNS:
                return [
                  { field: "name", headerName: "Campaign Name" },
                  { field: "description", headerName: "Description" },
                  { field: "createdAt", headerName: "Created" },
                  { field: "updatedAt", headerName: "Last Updated" },
                  { field: "actions", headerName: "Actions" },
                ];
              default:
                return [];
            }
          }, [activeTab])}
          columnVisibility={currentColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          activeTab={activeTab}
          onImportComplete={handleImportComplete}
        />

        {/* YouTube Search Modal */}
        <YouTubeSearchModal
          open={youtubeSearchOpen}
          onClose={handleCloseYoutubeSearch}
          reactionChannels={outreachData.reactionChannels}
          playlistingChannels={outreachData.playlistingChannels}
          onChannelAdded={handleChannelAdded}
          activeCategory={getCurrentCategory()}
        />
        <SpotifyPlaylistSearchModal
          open={spotifySearchOpen}
          onClose={handleCloseSpotifySearch}
          existingPlaylists={outreachData.spotifyPlaylists}
          onPlaylistAdded={outreachData.refreshData}
        />
      </Box>
    </Container>
  );
};

export default OutreachPage;
